import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { useFieldErrorMessage } from './useFieldErrorMessage';
import {
  FormFieldTextarea,
  type FormFieldTextareaProps
} from '@/components/form/FormFieldTextarea';

export interface FormTextareaProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<FormFieldTextareaProps, 'name' | 'value' | 'onChange' | 'error'> {
  /**
   * React Hook Form control object
   */
  control: Control<TFieldValues>;

  /**
   * Field name in the form
   */
  name: TName;

  /**
   * Default value for the field
   */
  defaultValue?: string;
}

/**
 * React Hook Form adapter for FormFieldTextarea
 * Automatically handles form state, validation, and errors
 *
 * @example
 * ```tsx
 * const { control } = useForm<FormData>();
 *
 * <FormTextarea
 *   control={control}
 *   name="description"
 *   label="Description"
 *   required
 * />
 * ```
 */
export function FormTextarea<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({ control, name, defaultValue, ...fieldProps }: FormTextareaProps<TFieldValues, TName>) {
  const fieldErrorMessage = useFieldErrorMessage();

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue as never}
      render={({ field, fieldState }) => {
        const errorMessage = fieldErrorMessage(fieldState.error);

        return <FormFieldTextarea {...field} {...fieldProps} error={errorMessage} />;
      }}
    />
  );
}

FormTextarea.displayName = 'FormTextarea';
